"use client"

import { Radio as RadioPrimitive } from "@base-ui/react/radio"
import { RadioGroup as RadioGroupPrimitive } from "@base-ui/react/radio-group"

import { cn } from "@/lib/utils"

type OnValueChange<TValue extends string> = {
	bivarianceHack: (value: TValue) => void;
}["bivarianceHack"];

interface RadioGroupProps<TValue extends string = string>
	extends Omit<
		RadioGroupPrimitive.Props,
		"value" | "defaultValue" | "onValueChange"
	> {
	value?: TValue;
	defaultValue?: TValue;
	onValueChange?: OnValueChange<TValue>;
	orientation?: "horizontal" | "vertical";
}

function RadioGroup<TValue extends string = string>({
	className,
	onValueChange,
	orientation = "vertical",
	...props
}: RadioGroupProps<TValue>) {
  return (
    <RadioGroupPrimitive
      data-slot="radio-group"
      data-orientation={orientation}
      aria-orientation={orientation}
      className={cn(
        "w-full",
        orientation === "horizontal"
          ? "flex flex-row items-center gap-3"
          : "grid gap-3",
        className
      )}
      onValueChange={
        onValueChange
          ? (value) => {
              onValueChange(String(value) as TValue)
            }
          : undefined
      }
      {...props}
    />
  )
}

function RadioGroupItem({ className, ...props }: RadioPrimitive.Root.Props) {
  return (
    <RadioPrimitive.Root
      data-slot="radio-group-item"
      className={cn(
        "border-input dark:bg-input/30 data-checked:bg-primary data-checked:text-primary-foreground dark:data-checked:bg-primary data-checked:border-primary aria-invalid:aria-checked:border-primary aria-invalid:border-destructive focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 dark:aria-invalid:border-destructive/50 flex size-4 rounded-full focus-visible:ring-3 aria-invalid:ring-3 group/radio-group-item peer relative aspect-square shrink-0 border outline-none after:absolute after:-inset-x-3 after:-inset-y-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    >
      <RadioPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="flex size-4 items-center justify-center"
      >
        <span className="bg-primary-foreground absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full" />
      </RadioPrimitive.Indicator>
    </RadioPrimitive.Root>
  )
}

export { RadioGroup, RadioGroupItem }
